import { z } from 'zod/v3';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import { ok } from 'neverthrow';
import { CocoChatModel } from '../models/coco-chat.model';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import { hasContentToSubmit } from '../utils/prompt-utils';
import { SubmitCocoChatAction } from './coco-submit-chat.action';
import {
  CocoEvent,
  cocoActionMetadata,
  getChatSurface,
} from '@cotera/client/app/etc';

const stopCocoInputSchema = z.object({
  text: z.string().optional(),
  editorState: z
    .object({
      type: z.literal('doc'),
      content: z.array(z.unknown()).optional(),
    })
    .optional(),
  files: z.array(z.record(z.string(), z.unknown())).optional(),
  systemSuffix: z.string().optional(),
});

type Params = z.infer<typeof stopCocoInputSchema>;

export class StopAction extends BaseAction<
  Params,
  { t: string; message?: string }[]
> {
  title = 'Stop Generating';
  inputSchema = stopCocoInputSchema;
  icon = 'stop';
  group = 'Chat';
  description = 'Stop the current chat generation';

  constructor(private readonly client: TenantedClient['resource']['chat']) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(CocoChatModel);
  }

  async execute(
    params: Params,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return ok({});
    }

    const status = model.statusWatchable.snapshot();
    if (status === 'streaming' || status === 'submitted') {
      // Optimistic: update UI immediately, fire stop request in background
      model.statusWatchable.set('ready');
      // Flag the conversation as locally-cancelled so "in progress" UI
      // (thinking dots, stack-frame header phrases) hides immediately
      // instead of waiting for server cancellation events to arrive.
      model.cancelledWatchable.set(true);
      await this.client.stopChat({
        conversationId: model.conversationId,
      });
    }

    if (!hasContentToSubmit(params)) {
      return ok({});
    }

    const submitAction = context.getAction(SubmitCocoChatAction);
    const submitResult = await submitAction.askOrExecute(
      {
        text: params.text,
        editorState: params.editorState,
        files: params.files,
        systemSuffix: params.systemSuffix,
      },
      context
    );

    if (submitResult.isErr()) {
      return submitResult;
    }

    return ok({});
  }

  track({
    context,
  }: ActionTrackingContext<
    Params,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return null;
    }

    return cocoActionMetadata(CocoEvent.ChatStopped, {
      conversationId: model.conversationId,
      surface: getChatSurface(context),
      hasMessages: model.hasMessages.snapshot(),
    });
  }
}
